import React, { useEffect, useMemo, useRef } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  InputNumber,
  Table,
  Tag,
  message,
  Spin,
  Divider,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { MinusCircleOutlined, PlusOutlined, SaveOutlined, SendOutlined, CheckOutlined, RollbackOutlined } from '@ant-design/icons';
import stockAdjustmentService from '@/services/stockAdjustment.service';
import inventoryService, { type HeatStockRow, type Item } from '@/services/inventory.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_STOCK_ADJUSTMENT } from '@/constants/approval-entity-types';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_STOCK_ADJUSTMENT } from '@/constants/document-entity-types';
import { useWarehouses, useItems } from '@/hooks/useInventory';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import DocumentUpload from '@/components/documents/DocumentUpload';
import api from '@/services/api.service';

const { Title, Text } = Typography;
const { TextArea } = Input;

type StockRow = { id?: string; item_id?: string; available_qty?: number };

type AdjustmentLine = {
  item_id?: string;
  heat_number?: string;
  dimension?: string;
  location_id?: string;
  location_label?: string;
  system_quantity?: number;
  reserved_quantity?: number;
  actual_quantity?: number;
  unit_cost?: number;
  uom?: string;
  bucket_locked?: boolean;
  mtc_document_id?: string | null;
  mtc_document_number?: string | null;
  linked_mtc_document_id?: string | null;
  linked_mtc_document_number?: string | null;
  linked_mtc_reference_number?: string | null;
};

function isHeatTracked(item?: Pick<Item, 'is_critical' | 'heat_number_mandatory'> | null): boolean {
  return isItemCritical(item) && Boolean(item?.heat_number_mandatory);
}

function itemProfileDimension(item?: Pick<Item, 'profile_dimension'> | null): string {
  return String(item?.profile_dimension ?? '').trim();
}

function bucketKey(line: AdjustmentLine): string {
  return [line.item_id, line.location_id || '', String(line.heat_number || '').trim().toUpperCase(), String(line.dimension || '').trim().toUpperCase()].join('|');
}

function blankHeatLine(item: Item, itemId: string): AdjustmentLine {
  return {
    item_id: itemId,
    heat_number: '',
    dimension: itemProfileDimension(item),
    location_id: '',
    location_label: '',
    system_quantity: 0,
    reserved_quantity: 0,
    actual_quantity: 0,
    unit_cost: Number(item.standard_cost || 0),
    uom: item.base_uom,
    bucket_locked: false,
    mtc_document_id: null,
    mtc_document_number: null,
    linked_mtc_document_id: null,
    linked_mtc_document_number: null,
    linked_mtc_reference_number: null,
  };
}

function lineFromBucket(item: Item, itemId: string, row: HeatStockRow): AdjustmentLine {
  return {
    item_id: itemId,
    heat_number: row.heat_number,
    dimension: row.dimension,
    location_id: row.location_id || '',
    location_label: [row.location_code, row.location_name].filter(Boolean).join(' — '),
    system_quantity: Number(row.on_hand_quantity || 0),
    reserved_quantity: Number(row.reserved_quantity || 0),
    actual_quantity: Number(row.on_hand_quantity || 0),
    unit_cost: Number(row.average_cost || 0) || Number(item.standard_cost || 0),
    uom: row.uom || item.base_uom,
    bucket_locked: true,
    mtc_document_id: null,
    mtc_document_number: null,
    linked_mtc_document_id: row.mtc_document_id,
    linked_mtc_document_number: row.mtc_document_number,
    linked_mtc_reference_number: row.mtc_reference_number,
  };
}

function unwrapStock(res: unknown): StockRow[] {
  const data = (res as any)?.data ?? res;
  const inner = data?.data ?? data;
  return Array.isArray(inner) ? inner : [];
}

const ADJ_TYPES = [
  { value: 'Physical Count', label: 'Physical count' },
  { value: 'Damage', label: 'Damage' },
  { value: 'Obsolescence', label: 'Obsolescence' },
  { value: 'Theft', label: 'Theft' },
  { value: 'Found', label: 'Found' },
  { value: 'System Correction', label: 'System correction' },
];

const StockAdjustmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  useApplyUserDefaultDepartment(form, isNew);
  const queryClient = useQueryClient();
  const warehouseId = Form.useWatch('warehouse_id', form);

  const { data: warehouses = [] } = useWarehouses();
  const { data: items = [] } = useItems({ is_active: true });
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const { data: reasonCodes = [] } = useQuery({
    queryKey: ['stock-adjustment-reason-codes'],
    queryFn: () => stockAdjustmentService.listReasonCodes(),
  });

  const { data: adjustment, isLoading } = useQuery({
    queryKey: ['stock-adjustment', id],
    queryFn: () => stockAdjustmentService.getById(id!),
    enabled: !isNew && !!id,
  });

  const { data: stockRows = [], isLoading: stockLoading } = useQuery({
    queryKey: ['items', 'stock', warehouseId],
    queryFn: async () => {
      const res = await api.getClient().get('/inventory/items/stock', {
        params: warehouseId ? { warehouse_id: warehouseId } : {},
      });
      return unwrapStock(res);
    },
    enabled: !!warehouseId,
  });

  const stockByItemId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of stockRows) {
      const iid = (r as any).item_id ?? r.id;
      if (!iid) continue;
      const q = Number((r as any).available_qty ?? 0);
      m.set(String(iid), q);
    }
    return m;
  }, [stockRows]);

  const expandGen = useRef(0);
  const warehouseSeen = useRef<string | null>(null);

  useEffect(() => {
    if (!warehouseId) return;
    if (warehouseSeen.current && warehouseSeen.current !== warehouseId && (isNew || !adjustment || adjustment.adjustment_status === 'Draft')) {
      form.setFieldValue('items', [{ actual_quantity: 0, unit_cost: 0 }]);
      message.info('Lines were cleared so quantities match the selected warehouse.');
    }
    warehouseSeen.current = warehouseId;
  }, [warehouseId, isNew, adjustment, form]);

  const applyItemSelection = async (index: number, itemId: string) => {
    const item = itemsById.get(itemId);
    const gen = ++expandGen.current;
    const current = () => (form.getFieldValue('items') || []) as AdjustmentLine[];
    if (!itemId || !item) return;

    if (!isHeatTracked(item)) {
      const sys = stockByItemId.get(itemId) ?? 0;
      const next = [...current()];
      const prev = next[index] || {};
      next[index] = {
        item_id: itemId,
        heat_number: '',
        dimension: itemProfileDimension(item),
        location_id: '',
        location_label: '',
        system_quantity: sys,
        reserved_quantity: 0,
        actual_quantity: prev.item_id === itemId ? Number(prev.actual_quantity ?? sys) : sys,
        unit_cost: prev.item_id === itemId ? prev.unit_cost : Number(item.standard_cost || 0),
        uom: item.base_uom,
        bucket_locked: false,
        mtc_document_id: null,
        mtc_document_number: null,
        linked_mtc_document_id: null,
        linked_mtc_document_number: null,
        linked_mtc_reference_number: null,
      };
      form.setFieldsValue({ items: next });
      return;
    }

    if (!warehouseId) {
      message.warning('Choose a warehouse before a critical item.');
      return;
    }

    const rows = await inventoryService.getHeatWiseStock({ item_id: itemId, warehouse_id: warehouseId });
    if (gen !== expandGen.current) return;
    const latest = current();
    if (latest[index]?.item_id && latest[index]?.item_id !== itemId) return;

    const companyRows = rows.filter((r) => (r.ownership_type || 'COMPANY') === 'COMPANY');
    const orphanQty = companyRows
      .filter((r) => !String(r.heat_number || '').trim())
      .reduce((sum, r) => sum + Number(r.on_hand_quantity || 0), 0);
    if (orphanQty > 0) {
      message.warning(
        'Some on-hand quantity for this item has no heat number, so it is not listed. Enter the heat and dimension you counted.'
      );
    }

    const taken = new Set(latest.filter((_, i) => i !== index).map((line) => bucketKey(line)));
    let built = companyRows
      .filter((r) => String(r.heat_number || '').trim())
      .map((r) => lineFromBucket(item, itemId, r))
      .filter((line) => !taken.has(bucketKey(line)));
    if (!built.length) {
      if (companyRows.some((r) => String(r.heat_number || '').trim())) {
        message.info('Those heat lines are already on this adjustment.');
      }
      built = [blankHeatLine(item, itemId)];
    }
    form.setFieldsValue({ items: [...latest.slice(0, index), ...built, ...latest.slice(index + 1)] });
  };

  const addHeatLine = (index: number) => {
    const items = (form.getFieldValue('items') || []) as AdjustmentLine[];
    const row = items[index];
    const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
    if (!row?.item_id || !item) return;
    const next = [...items];
    next.splice(index + 1, 0, blankHeatLine(item, row.item_id));
    form.setFieldsValue({ items: next });
  };

  const setLineMtc = (index: number, docId: string | null, docNumber?: string | null) => {
    const items = (form.getFieldValue('items') || []) as AdjustmentLine[];
    const next = [...items];
    next[index] = {
      ...next[index],
      mtc_document_id: docId,
      mtc_document_number: docNumber ?? (docId ? next[index]?.mtc_document_number : null),
    };
    form.setFieldsValue({ items: next });
  };

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        adjustment_date: dayjs(),
        adjustment_type: 'Physical Count',
        items: [{ line_number: 1, actual_quantity: 0, unit_cost: 0 }],
      });
      return;
    }
    if (adjustment) {
      form.setFieldsValue({
        adjustment_date: adjustment.adjustment_date ? dayjs(adjustment.adjustment_date) : dayjs(),
        posting_date: adjustment.posting_date ? dayjs(adjustment.posting_date) : undefined,
        reference_number: adjustment.reference_number,
        warehouse_id: adjustment.warehouse_id,
        department_id: adjustment.department_id || undefined,
        adjustment_type: adjustment.adjustment_type,
        reason_code: adjustment.reason_code,
        reason: adjustment.reason,
        notes: adjustment.notes,
        items: (adjustment.items || []).map((it) => ({
          ...it,
          key: it.id,
          bucket_locked: Boolean(String(it.heat_number || '').trim()) && Number(it.system_quantity) > 0,
          location_label: it.location_label || '',
        })),
      });
    }
  }, [isNew, adjustment, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const rows = (values.items as AdjustmentLine[]) || [];
      for (const row of rows) {
        const item = row.item_id ? itemsById.get(row.item_id) : undefined;
        if (!isHeatTracked(item)) continue;
        if (!String(row.heat_number || '').trim()) {
          throw new Error(`Heat number is required for ${item?.item_code || 'a critical item'}`);
        }
        if (!String(row.dimension || '').trim()) {
          throw new Error(`Dimension is required for ${item?.item_code || 'a critical item'}`);
        }
      }
      const itemsPayload = rows.map((row, idx) => ({
        line_number: idx + 1,
        item_id: row.item_id,
        actual_quantity: Number(row.actual_quantity),
        system_quantity: row.system_quantity != null ? Number(row.system_quantity) : undefined,
        unit_cost: row.unit_cost != null ? Number(row.unit_cost) : undefined,
        uom: row.uom,
        heat_number: row.heat_number || '',
        dimension: row.dimension || '',
        location_id: row.location_id || undefined,
        mtc_document_id: row.mtc_document_id || undefined,
      }));
      const body = {
        adjustment_date: (values.adjustment_date as any)?.format?.('YYYY-MM-DD') ?? values.adjustment_date,
        posting_date: (values.posting_date as any)?.format?.('YYYY-MM-DD') ?? values.posting_date ?? null,
        reference_number: values.reference_number,
        warehouse_id: values.warehouse_id,
        department_id: values.department_id,
        adjustment_type: values.adjustment_type,
        reason_code: values.reason_code,
        reason: values.reason,
        notes: values.notes,
        items: itemsPayload,
      };
      if (isNew) return stockAdjustmentService.create(body);
      return stockAdjustmentService.update(id!, body);
    },
    onSuccess: (data) => {
      message.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
      if (isNew) navigate(`/scm/warehouse/stock-adjustment/${data.id}`, { replace: true });
      else queryClient.invalidateQueries({ queryKey: ['stock-adjustment', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const actionMutation = useMutation({
    mutationFn: async (action: 'submit' | 'approve' | 'post' | 'cancel') => {
      if (action === 'submit') return stockAdjustmentService.submit(id!);
      if (action === 'approve') return stockAdjustmentService.approve(id!);
      if (action === 'post') return stockAdjustmentService.post(id!);
      return stockAdjustmentService.cancel(id!);
    },
    onSuccess: () => {
      message.success('OK');
      queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
      queryClient.invalidateQueries({ queryKey: ['stock-adjustment', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Action failed'),
  });

  const status = adjustment?.adjustment_status;
  const isDraft = !status || status === 'Draft';
  const showUniversalApproval = !!adjustment?.approval_instance_id;

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {isNew ? 'New stock adjustment' : adjustment?.adjustment_number}
              </Title>
              {!isNew && status && <Tag>{status}</Tag>}
            </div>
            <Button onClick={() => navigate('/scm/warehouse/stock-adjustment')}>Back to list</Button>
          </Space>

          <Form form={form} layout="vertical" disabled={!isNew && !isDraft} onFinish={(v) => saveMutation.mutate(v)}>
            <Space wrap style={{ width: '100%' }}>
              <Form.Item name="adjustment_date" label="Adjustment date" rules={[{ required: true }]}>
                <DatePicker style={{ width: 160 }} />
              </Form.Item>
              <Form.Item name="posting_date" label="Posting date (optional)">
                <DatePicker style={{ width: 160 }} />
              </Form.Item>
              <Form.Item name="warehouse_id" label="Warehouse" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: 220 }}
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: `${w.warehouse_code} — ${w.warehouse_name}`,
                  }))}
                  disabled={!isNew && !isDraft}
                />
              </Form.Item>
              <Form.Item
                name="department_id"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <DepartmentSelect style={{ width: 280 }} disabled={!isNew && !isDraft} />
              </Form.Item>
              <Form.Item name="adjustment_type" label="Adjustment type" rules={[{ required: true }]}>
                <Select options={ADJ_TYPES} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="reference_number" label="Reference #">
                <Input style={{ width: 200 }} />
              </Form.Item>
            </Space>
            <Space wrap style={{ width: '100%' }}>
              <Form.Item name="reason_code" label="Reason code">
                <Select
                  allowClear
                  style={{ width: 280 }}
                  options={reasonCodes.map((r) => ({ value: r.code, label: `${r.code} — ${r.label}` }))}
                />
              </Form.Item>
            </Space>
            <Form.Item name="reason" label="Reason / narration" rules={[{ required: true }]}>
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} />
            </Form.Item>

            <Divider orientation="left">Lines</Divider>
            <Text type="secondary">
              Critical material is listed by heat number, dimension and MTC. System quantity is that bucket, not the
              warehouse total. Enter the counted quantity on each line. A decrease keeps the existing certificate. Increasing
              a heat that has no MTC needs the certificate when the item requires one.
            </Text>
            {warehouseId && stockLoading ? <Spin size="small" style={{ marginLeft: 8 }} /> : null}

            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  <Table
                    size="small"
                    pagination={false}
                    scroll={{ x: 1280 }}
                    dataSource={fields.map((f) => ({ ...f, key: f.key }))}
                    rowClassName={(record) => {
                      const itemId = form.getFieldValue(['items', record.name, 'item_id']);
                      return getCriticalItemRowClassName({
                        is_critical: isItemCritical(itemsById.get(itemId)),
                      });
                    }}
                    columns={[
                      {
                        title: '#',
                        width: 40,
                        render: (_: unknown, __: unknown, i: number) => i + 1,
                      },
                      {
                        title: 'Item',
                        width: 280,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              const critical = isItemCritical(item);
                              return (
                                <Space direction="vertical" size={4}>
                                  <Space size={6} wrap>
                                    <Form.Item name={[record.name, 'item_id']} rules={[{ required: true }]} noStyle>
                                      <ItemSelect
                                        items={items}
                                        autoLoad={false}
                                        style={{ width: 220 }}
                                        disabled={!isNew && !isDraft}
                                        onChange={(value) => {
                                          void applyItemSelection(record.name, String(value || ''));
                                        }}
                                      />
                                    </Form.Item>
                                    <CriticalItemTag critical={critical} />
                                  </Space>
                                  {isHeatTracked(item) && (isNew || isDraft) ? (
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => addHeatLine(record.name)}>
                                      Add heat
                                    </Button>
                                  ) : null}
                                  <Form.Item name={[record.name, 'location_id']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'location_label']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'system_quantity']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'reserved_quantity']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'uom']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'mtc_document_id']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'mtc_document_number']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'linked_mtc_document_id']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'linked_mtc_document_number']} hidden>
                                    <Input />
                                  </Form.Item>
                                  <Form.Item name={[record.name, 'linked_mtc_reference_number']} hidden>
                                    <Input />
                                  </Form.Item>
                                </Space>
                              );
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Heat',
                        width: 140,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              if (!isHeatTracked(item)) return <Text type="secondary">—</Text>;
                              return (
                                <Form.Item name={[record.name, 'heat_number']} noStyle>
                                  <Input
                                    placeholder="Heat number"
                                    disabled={Boolean(row?.bucket_locked) || (!isNew && !isDraft)}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Dimension',
                        width: 140,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              if (!row?.item_id) return <Text type="secondary">—</Text>;
                              if (!isHeatTracked(item)) {
                                return <Text>{itemProfileDimension(item) || '—'}</Text>;
                              }
                              return (
                                <Form.Item name={[record.name, 'dimension']} noStyle>
                                  <Input
                                    placeholder="Dimension"
                                    disabled={Boolean(row?.bucket_locked) || (!isNew && !isDraft)}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'UoM',
                        width: 80,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              return <Text>{row?.uom || item?.base_uom || '—'}</Text>;
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Location',
                        width: 120,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              return <Text>{row?.location_label || '—'}</Text>;
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'MTC',
                        width: 220,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              if (!isHeatTracked(item)) return <Text type="secondary">—</Text>;
                              const linkedId = row?.linked_mtc_document_id;
                              const ownId = row?.mtc_document_id;
                              const certId = linkedId || ownId;
                              const certNo = linkedId ? row?.linked_mtc_document_number : row?.mtc_document_number;
                              const certRef = linkedId ? row?.linked_mtc_reference_number : null;
                              const canEditMtc = (isNew || isDraft) && !linkedId;
                              return (
                                <Space direction="vertical" size={4}>
                                  {certId ? (
                                    <MtcDocumentLink documentId={certId} documentNumber={certNo} compact />
                                  ) : item?.mtc_required_on_receipt ? (
                                    <Tag color="orange">MTC required to increase</Tag>
                                  ) : (
                                    <Text type="secondary">Optional</Text>
                                  )}
                                  {certRef ? <Text type="secondary">{certRef}</Text> : null}
                                  {canEditMtc ? (
                                    <Space wrap size={4}>
                                      <Input
                                        size="small"
                                        placeholder="Doc # or UUID"
                                        style={{ width: 120 }}
                                        value={row?.mtc_document_number || row?.mtc_document_id || ''}
                                        onChange={(e) => setLineMtc(record.name, e.target.value.trim() || null, e.target.value)}
                                      />
                                      <DocumentUpload
                                        entityType={adjustment?.id ? 'StockAdjustment' : undefined}
                                        entityId={adjustment?.id}
                                        buttonText="Upload MTC"
                                        defaultValues={{ document_type: 'Certificate', document_category: 'MTC' }}
                                        onSuccess={(doc) => {
                                          if (doc?.id) setLineMtc(record.name, doc.id, doc.document_number);
                                        }}
                                      />
                                    </Space>
                                  ) : null}
                                </Space>
                              );
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'System',
                        width: 90,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              if (!row?.item_id) return <Text>—</Text>;
                              if (row.system_quantity != null) return <Text>{Number(row.system_quantity)}</Text>;
                              if (!isHeatTracked(item)) return <Text>{stockByItemId.get(String(row.item_id)) ?? 0}</Text>;
                              return <Text>—</Text>;
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Reserved',
                        width: 90,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              const item = row?.item_id ? itemsById.get(row.item_id) : undefined;
                              if (!isHeatTracked(item)) return <Text type="secondary">—</Text>;
                              return <Text>{Number(row?.reserved_quantity || 0)}</Text>;
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Actual qty',
                        width: 110,
                        render: (_, record) => (
                          <Form.Item name={[record.name, 'actual_quantity']} rules={[{ required: true }]} noStyle>
                            <InputNumber min={0} style={{ width: 90 }} disabled={!isNew && !isDraft} />
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Variance',
                        width: 90,
                        render: (_, record) => (
                          <Form.Item noStyle shouldUpdate={(p, c) => p.items !== c.items}>
                            {() => {
                              const row = (form.getFieldValue('items') || [])[record.name] as AdjustmentLine | undefined;
                              if (row?.system_quantity == null || row.actual_quantity == null) return <Text>—</Text>;
                              const variance = Number(row.actual_quantity) - Number(row.system_quantity);
                              return <Text type={variance === 0 ? 'secondary' : undefined}>{variance}</Text>;
                            }}
                          </Form.Item>
                        ),
                      },
                      {
                        title: 'Unit cost',
                        width: 110,
                        render: (_, record) => (
                          <Form.Item name={[record.name, 'unit_cost']} noStyle>
                            <InputNumber min={0} style={{ width: 100 }} disabled={!isNew && !isDraft} />
                          </Form.Item>
                        ),
                      },
                      {
                        title: '',
                        width: 48,
                        render: (_, record) =>
                          isNew || isDraft ? (
                            <MinusCircleOutlined onClick={() => remove(record.name)} style={{ color: '#ff4d4f' }} />
                          ) : null,
                      },
                    ]}
                  />
                  {(isNew || isDraft) && (
                    <Button type="dashed" onClick={() => add({ actual_quantity: 0 })} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                      Add line
                    </Button>
                  )}
                </>
              )}
            </Form.List>

            {(isNew || isDraft) && (
              <Space style={{ marginTop: 16 }}>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saveMutation.isPending}>
                  Save draft
                </Button>
              </Space>
            )}
          </Form>

          {!isNew && adjustment && showUniversalApproval && (
            <div style={{ marginBottom: 16 }}>
              <ApprovalPanel
                entityType={ENTITY_TYPE_STOCK_ADJUSTMENT}
                entityId={adjustment.id}
                onApproveViaDocument={async (remarks) => {
                  await stockAdjustmentService.approve(adjustment.id, remarks);
                  queryClient.invalidateQueries({ queryKey: ['stock-adjustment', id] });
                  queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
                }}
                onRejectViaDocument={async (payload) => {
                  await stockAdjustmentService.rejectApproval(adjustment.id, payload);
                  queryClient.invalidateQueries({ queryKey: ['stock-adjustment', id] });
                  queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
                }}
                extraInvalidateQueryKeys={[['stock-adjustment', id], ['stock-adjustments']]}
              />
            </div>
          )}

          {!isNew && adjustment && (
            <Space wrap>
              {status === 'Draft' && (
                <Button icon={<SendOutlined />} onClick={() => actionMutation.mutate('submit')}>
                  Submit
                </Button>
              )}
              {(status === 'Draft' || status === 'Submitted') && !showUniversalApproval && (
                <Button type="primary" icon={<CheckOutlined />} onClick={() => actionMutation.mutate('approve')}>
                  Approve
                </Button>
              )}
              {status === 'Approved' && (
                <Button type="primary" onClick={() => actionMutation.mutate('post')}>
                  Post to stock
                </Button>
              )}
              {status !== 'Posted' && status !== 'Cancelled' && (
                <Button icon={<RollbackOutlined />} onClick={() => actionMutation.mutate('cancel')}>
                  Cancel doc
                </Button>
              )}
            </Space>
          )}
        </Space>
      </Card>

      {!isNew && adjustment?.id && (
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_STOCK_ADJUSTMENT}
          entityId={adjustment.id}
          readOnly={adjustment.adjustment_status === 'Posted' || adjustment.adjustment_status === 'Cancelled'}
          queryKeyPrefix={['documents', 'stock-adjustment']}
        />
      )}
    </div>
  );
};

export default StockAdjustmentDetailPage;
